import React, { useEffect, useMemo } from 'react';
import { Crown, Sparkles, X } from 'lucide-react';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
};

const confettiLayout = [
  { left: '10%', delay: '0ms', color: 'bg-pink-400' },
  { left: '18%', delay: '160ms', color: 'bg-indigo-400' },
  { left: '26%', delay: '320ms', color: 'bg-emerald-400' },
  { left: '34%', delay: '80ms', color: 'bg-amber-400' },
  { left: '42%', delay: '240ms', color: 'bg-rose-400' },
  { left: '50%', delay: '400ms', color: 'bg-violet-400' },
  { left: '58%', delay: '120ms', color: 'bg-sky-400' },
  { left: '66%', delay: '280ms', color: 'bg-lime-400' },
  { left: '74%', delay: '440ms', color: 'bg-fuchsia-400' },
  { left: '82%', delay: '200ms', color: 'bg-teal-400' },
  { left: '90%', delay: '360ms', color: 'bg-orange-400' }
];

const PremiumCelebrationModal: React.FC<Props> = ({ isOpen, onClose, title, subtitle }) => {
  const confetti = useMemo(() => confettiLayout, []);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center px-4">
      <style>{`
        @keyframes anivista-pop {
          0% { opacity: 0; transform: translateY(10px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes anivista-glow {
          0% { transform: scale(1); filter: drop-shadow(0 0 0 rgba(99,102,241,0)); }
          50% { transform: scale(1.03); filter: drop-shadow(0 12px 28px rgba(99,102,241,.35)); }
          100% { transform: scale(1); filter: drop-shadow(0 0 0 rgba(99,102,241,0)); }
        }
        @keyframes anivista-confetti {
          0% { transform: translateY(-18px) rotate(0deg); opacity: 0; }
          15% { opacity: 1; }
          85% { opacity: 1; }
          100% { transform: translateY(760px) rotate(720deg); opacity: 0; }
        }
      `}</style>

      <button
        type="button"
        aria-label="close"
        onClick={onClose}
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
      />

      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-sm overflow-hidden rounded-[2rem] bg-white shadow-2xl border border-gray-100"
        style={{ animation: 'anivista-pop 240ms ease-out both' }}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-2 rounded-xl text-gray-500 hover:bg-gray-50 active:scale-95 transition"
          aria-label="Fermer"
        >
          <X size={18} />
        </button>

        <div className="relative px-6 pt-7 pb-6">
          <div className="absolute inset-x-0 -top-20 h-40 bg-gradient-to-b from-indigo-500/20 via-indigo-500/10 to-transparent" />

          <div className="relative flex flex-col items-center text-center">
            <div
              className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 text-white flex items-center justify-center"
              style={{ animation: 'anivista-glow 900ms ease-out both' }}
            >
              <Crown size={26} />
              <div className="absolute -right-2 -top-2 bg-white rounded-xl p-1 shadow">
                <Sparkles size={14} className="text-amber-500" />
              </div>
            </div>

            <h3 className="mt-4 text-xl font-black tracking-tight text-gray-900">
              {title ?? 'Bienvenue dans Premium !'}
            </h3>
            <p className="mt-2 text-sm text-gray-600 leading-relaxed">
              {subtitle ?? "Ton abonnement est actif. Tu débloques toutes les fonctionnalités."}
            </p>

            <div className="mt-5 w-full">
              <button
                type="button"
                onClick={onClose}
                className="w-full bg-indigo-600 text-white py-3 rounded-2xl font-bold shadow-lg hover:bg-indigo-700 active:scale-[0.99] transition"
              >
                C'est parti
              </button>
              <p className="mt-3 text-[11px] text-gray-400 font-semibold">
                Merci pour ton soutien
              </p>
            </div>
          </div>

          <div className="pointer-events-none absolute inset-0">
            {confetti.map((c, i) => (
              <span
                key={i}
                className={`absolute top-0 ${c.color} w-2 h-4 rounded-sm opacity-0`}
                style={{
                  left: c.left,
                  animation: `anivista-confetti 2200ms cubic-bezier(.2,.8,.2,1) ${c.delay} both`
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumCelebrationModal;
